import React, { useState, useEffect } from 'react';
import { SucursalService } from "../../services/sucursalService";
import { Categorias } from '../../models/Categorias';
import theme, { cn } from '../../styles/theme';

const CategoriaForm = ({ categoria, onSubmit, onCancel, isLoading, sucursal: propSucursal, sucursalId, sucursalNombre}) => {
  const [formData, setFormData] = useState({
    Id_categoria: '',
    Nombre_categoria: '',
    Estado: 'ACTIVO',
    Id_sucursal: sucursalId || '',
    Nombre_empresa: '',
    Nombre_sucursal: ''
  });

  const [sucursales, setSucursal] = useState(propSucursal || []);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!propSucursal || propSucursal.length === 0) {
      const fetchSucursal = async () => {
        try {
          const data = await SucursalService.getById(sucursalId);
          setSucursal(data);
        } catch (error) {
          console.error("Error cargando sucursal:", error);
        }
      };
      fetchSucursal();
    }
  }, [propSucursal, sucursalId]);

  useEffect(() => {
    if (categoria) {
      const Instance = new Categorias(categoria);
      
      setFormData({
        Id_categoria: Instance.Id_categoria,
        Nombre_categoria: Instance.Nombre_categoria,
        Estado: Instance.Estado,
        Id_sucursal: Instance.Id_sucursal || sucursalId,
        Nombre_empresa: Instance.Nombre_empresa,
        Nombre_sucursal: Instance.Nombre_sucursal
      });
    } else {
      setFormData({
        Id_categoria: '',
        Nombre_categoria: '',
        Estado: 'ACTIVO',
        Id_sucursal: sucursalId || '',
        Nombre_empresa: '',
        Nombre_sucursal: ''
      });
    }
  }, [categoria, sucursalId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    if (name === 'Id_sucursal' && value !== '') {
      processedValue = parseInt(value, 10);
    }
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
       
    if (!formData.Nombre_categoria.trim()) {
      newErrors.Nombre_categoria = 'El nombre es requerido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const empreToSubmit = new Categorias({
        ...formData,
        Id_sucursal: sucursalId
      });
      
      onSubmit(empreToSubmit);
    }
  };

  const getSucursalName = (sucursalId) => {
    const sucursal = sucursales.find(r => r.Id_sucursal === parseInt(sucursalId));
    return sucursal ? sucursal.Nombre_sucursal : 'sucursal no encontrado';
  };

  return (
    <div className={cn(theme.classes.card.base, theme.shadows.lg, "p-8 max-w-4xl mx-auto")}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className={cn(theme.classes.iconBox.primary, "p-3")}>
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        </div>
        <div>
          <h3 className="text-2xl font-bold text-gray-900">
            {categoria ? 'Editar Categoría' : 'Crear Nueva Categoría'}
          </h3>
          <p className="text-gray-600 mt-1">
            {categoria ? 'Modifica la información de la categoría seleccionada' : 'Completa los datos de la nueva categoría'}
          </p>
        </div>
      </div>
      
      {categoria && (
        <div className="bg-gray-50 border-l-4 border-gray-400 p-4 mb-6 rounded-r-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-gray-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm text-gray-700 font-medium">Información de Debug</p>
              <p className="text-sm text-gray-600 mt-1">
                Editando categoría ID: <span className="font-mono font-semibold">{formData.Id_categoria}</span> | 
                Nombre: <span className="font-semibold">{formData.Nombre_categoria}</span>
              </p>
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Primera fila - Nombre y Estado */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Campo Nombre de la Categoría */}
          <div className="space-y-2">
            <label className={theme.classes.form.label}>
              Nombre de la Categoría
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="Nombre_categoria"
                value={formData.Nombre_categoria}
                onChange={handleChange}
                className={cn(
                  theme.classes.form.input,
                  errors.Nombre_categoria && theme.classes.form.inputError
                )}
                placeholder="Ej: Bebidas, Postres, Platos Principales..."
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
            </div>
            {errors.Nombre_categoria && (
              <p className="text-red-600 text-sm font-medium flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.Nombre_categoria}
              </p>
            )}
          </div>          
          
          {/* Campo Estado */}
          <div className="space-y-2">
            <label className={theme.classes.form.label}>
              Estado
            </label>
            <div className="relative">
              <select
                name="Estado"
                value={formData.Estado}
                onChange={handleChange}
                className={theme.classes.form.select}
              >
                <option value="ACTIVO">ACTIVO</option>
                <option value="INACTIVO">INACTIVO</option>
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            {/* Indicador visual del estado */}
            <div className="flex items-center gap-2">
              <div className={cn("w-3 h-3 rounded-full", formData.Estado === 'ACTIVO' ? 'bg-green-500' : 'bg-red-500')}></div>
              <span className={cn("text-sm font-medium", formData.Estado === 'ACTIVO' ? 'text-green-700' : 'text-red-700')}>
                {formData.Estado === 'ACTIVO' ? 'Activo' : 'Inactivo'}
              </span>
            </div>
          </div>
        </div>

        {/* Información de la sucursal (solo informativo) */}
        {sucursalId && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className={cn("p-2 bg-gray-100 rounded-lg")}>
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Sucursal asignada: <span className="font-semibold">{sucursalNombre}</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  ID de sucursal: {sucursalId}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Botones de acción */}
        <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className={cn(
              theme.classes.button.secondary,
              "px-6 py-3 font-semibold rounded-lg",
              theme.transitions.default,
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            Cancelar
          </button>
          
          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              theme.classes.button.primary,
              "px-8 py-3 font-semibold rounded-lg transform hover:-translate-y-0.5",
              theme.transitions.default,
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Guardando...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                {categoria ? 'Actualizar Categoría' : 'Crear Categoría'}
              </div>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CategoriaForm;